import { Component, DestroyRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DatePickerModule } from 'primeng/datepicker';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SupportHubStore } from '../support-hub.store';
import { FeedbackVersion, SupportHubRequest } from '../support-hub.models';
import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';

interface Ticket {
  id: string;
  title: string;
  statusId: number;
  status: string;
  priority: string;
  version: string;
  assignee?: string;
  requestTypeId: number;
  requestType: string;
  useCase?: string;
  applicationId: number;
  application?: string;
  teamId: number;
  team?: string;
  requestedUser: string;
  submitted: string;
  updated: string;
  likesCount?: number;
  commentCount?: number;
  followed?: boolean;
  liked?: boolean;
}

type SortableField = 'status_id' | 'workcategorytype_id' | 'application_id' | 'version_id' | 'creation_date';
type SearchFilterValue = number | string | string[];

@Component({
  selector: 'app-support-hub-list',
  standalone: true,
  imports: [FormsModule, MatProgressSpinnerModule, DatePickerModule],
  templateUrl: './support-hub-list.component.html',
  styleUrl: './support-hub-list.component.scss'
})
export class SupportHubListComponent {
  private readonly destroyRef = inject(DestroyRef);
  constructor(
    private readonly store: SupportHubStore,
    private readonly router: Router,
    private readonly apiService: MfgappsApiService
  ) {}

  searchTitle = '';
  selectedStatusId: number | null = null;
  selectedRequestTypeId: number | null = null;
  selectedApplicationId: number | null = null;
  selectedVersionId: number | null = null;
  selectedTeamId: number | null = null;
  selectedDateRange: (Date | null)[] | null = null;
  selectedSortBy = '';
  selectedSortOrder: 'ASC' | 'DESC' = 'ASC';
  showUseCase = false;
  showFilters = false;
  showFollowAction = false;
  selectedTicketId: string | null = null;

  page = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 1;
  versionsList: FeedbackVersion[] = [];
  rowsPerPageOptions = [10, 25, 50];
  loader = { isLoading: false, message: '' };

  private setLoader(message: string): void {
    this.loader.isLoading = true;
    this.loader.message = message;
  }

  private resetLoader(): void {
    this.loader.isLoading = false;
    this.loader.message = '';
  }

  async ngOnInit(): Promise<void> {
    this.restoreListState();
    this.store.selectedUseCaseIdSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async selectedUseCaseId => {
      if (!selectedUseCaseId || this.loader.isLoading) {
        return;
      }
      this.loadVersions().catch((error: any) => {
        console.info('Failed to load versions:', error);
      });
      this.page = 1;
      await this.goToPage(1);
    });
  }

  get statusOptions() {
    return this.store.statusOptions;
  }

  get requestTypeOptions(): Array<{ id: number; name: string }> {
    const unique = new Map(
      this.store.useCaseOptions
        .flatMap(useCase => useCase.workcategories)
        .filter(wc => wc.wctId && wc.wctName)
        .map(wc => [wc.wctId, wc.wctName])
    );
    return [...unique.entries()].map(([id, name]) => ({ id, name }));
  }

  get applicationOptions(): Array<{ id: number; name: string }> {
    const unique = new Map(
      this.store.useCaseOptions
        .flatMap(useCase => useCase.applications)
        .filter(app => app.appId && app.appName)
        .map(app => [app.appId, app.appName])
    );
    return [...unique.entries()].map(([id, name]) => ({ id, name }));
  }

  get versionOptions(): Array<{ id: number; name: string }> {
    const unique = new Map(
      this.versionsList
        .filter(version => version.version_id && version.version_name)
        .map(version => [version.version_id, version.version_name])
    );
    return [...unique.entries()].map(([id, name]) => ({ id, name }));
  }

  get teamOptions(): Array<{ id: number; name: string }> {
    const unique = new Map<number, string>();
    this.store.feedbackListValue.forEach(ticket => {
      if (ticket.assigned_team && ticket.assigned_team_name) {
        unique.set(ticket.assigned_team, ticket.assigned_team_name);
      }
    });
    return [...unique.entries()].map(([id, name]) => ({ id, name }));
  }

  get tickets(): Ticket[] {
    return this.store.feedbackListValue.map(request => this.toDashboardTicket(request));
  }

  get pageNumbers(): number[] {
    const maxVisible = 5;
    let start = Math.max(1, this.page - Math.floor(maxVisible / 2));
    const end = Math.min(this.totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  private toDashboardTicket(request: SupportHubRequest): Ticket {
    const latestActivity = request.activityLog?.[request.activityLog?.length - 1]?.log_date ?? request.creation_date;
    return {
      id: `#${request.feedback_id}`,
      title: request.title,
      statusId: request.status_id ?? 0,
      status: this.store.getStatusNameById(request.status_id, 'Unknown'),
      priority: this.store.getCriticalityNameById(request.rank_id),
      requestTypeId: request.workcategorytype_id ?? 0,
      requestType: this.store.getRequestTypeNameByIds(request.usecase_id, request.workcategorytype_id),
      useCase: this.store.getUseCaseNameById(request.usecase_id),
      applicationId: request.application_id ?? 0,
      application: this.store.getApplicationNameById(request.usecase_id, request.application_id),
      version: request.version ?? '',
      assignee: request.assigned_user_name || 'Unassigned',
      teamId: request.assigned_team ?? 0,
      team: request.assigned_team_name,
      requestedUser: request.requested_user_name,
      submitted: this.store.formatUpdatedAt(request.creation_date),
      updated: this.store.formatUpdatedAt(latestActivity),
      likesCount: request.likes_count,
      commentCount: request.public_comments_count,
      followed: request.is_following,
      liked: request.is_liked
    };
  }

  get pageStart(): number {
    if (this.totalCount === 0) {
      return 0;
    }
    return (this.page - 1) * this.rowsPerPage + 1;
  }

  get pageEnd(): number {
    if (this.totalCount === 0) {
      return 0;
    }
    return Math.min((this.page - 1) * this.rowsPerPage + this.tickets.length, this.totalCount);
  }

  get selectedTicket(): Ticket | undefined {
    return this.tickets.find((ticket) => ticket.id === this.selectedTicketId);
  }

  clearFilters(): void {
    this.selectedStatusId = null;
    this.selectedRequestTypeId = null;
    this.selectedApplicationId = null;
    this.selectedVersionId = null;
    this.selectedTeamId = null;
    this.selectedDateRange = null;
    this.selectedSortBy = '';
    this.selectedSortOrder = 'ASC';
    this.searchTitle = '';
    void this.goToPage(1);
  }

  onDateRangeChange(): void {
    void this.goToPage(1);
  }

  onFilterChange(): void {
    void this.goToPage(1);
  }

  onSortColumnClick(sortBy: SortableField): void {
    if (this.selectedSortBy === sortBy) {
      this.selectedSortOrder = this.selectedSortOrder === 'ASC' ? 'DESC' : 'ASC';
    }
    else {
      this.selectedSortBy = sortBy;
      this.selectedSortOrder = 'ASC';
    }
    void this.goToPage(1);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
    this.persistListState();
  }

  isSortActive(sortBy: SortableField): boolean {
    return this.selectedSortBy === sortBy;
  }

  openTicketPreview(ticket: Ticket): void {
    this.selectedTicketId = ticket.id;
  }

  closeTicketPreview(): void {
    this.selectedTicketId = null;
  }

  viewTicket(ticket: Ticket): void {
    this.router.navigate(['/support-hub/request/detail', this.store.parseTicketId(ticket.id)]);
  }

  isTicketSubmitter(request: SupportHubRequest): boolean {
    return request.requested_user_name.startsWith(this.store.currentUser);
  }

  canEditTicket(ticket: Ticket): boolean {
    const feedbackId = this.store.parseTicketId(ticket.id);
    const request = this.store.findById(feedbackId);
    if (!request) {
      return false;
    }

    const isAdmin = this.isAdminRole();
    const isRequester = this.isTicketSubmitter(request);
    const isNew = this.store.isStatus(request.status_id, this.store.statusIds.NEW);

    return isNew && (isRequester || isAdmin);
  }

  isDeveloperRole(): boolean {
    return this.store.currentUserRoleValue === SupportHubStore.ROLE_DEVELOPER;
  }

  isAdminRole(): boolean {
    return this.store.isAdminRoleName(this.store.currentUserRoleValue);
  }

  editTicket(ticket: Ticket): void {
    if (!this.canEditTicket(ticket)) {
      return;
    }
    this.router.navigate(['/support-hub/request/edit', this.store.parseTicketId(ticket.id)]);
  }

  openTicketAction(ticket: Ticket, fragment: 'actions' | 'threads', tab?: string): void {
    this.router.navigate(['/support-hub/request/detail', this.store.parseTicketId(ticket.id)], {
      fragment: fragment,
      queryParams: tab ? { tab } : undefined
    });
  }

  async toggleFollowTicket(ticket: Ticket): Promise<void> {
    const feedbackId = this.store.parseTicketId(ticket.id);
    const request = this.store.findById(feedbackId);
    if (!request || !this.showFollowAction) {
      return;
    }
    this.setLoader(ticket.followed ? 'Unfollowing...' : 'Following...');
    try {
      if (await this.store.toggleTicketFollow(request)) {
        ticket.followed = request.is_following = !ticket.followed;
      }
    }
    finally {
      this.resetLoader();
    }
  }

  async toggleLike(ticket: Ticket): Promise<void> {
    const feedbackId = this.store.parseTicketId(ticket.id);
    const request = this.store.findById(feedbackId);
    if (!request) {
      return;
    }
    const wasLiked = !!ticket.liked;
    const likeMsg = wasLiked ? 'Unliking' : 'Liking';
    this.setLoader(`${likeMsg} ticket...`);
    try {
      if (await this.store.toggleTicketLike(request)) {
        request.likes_count = Math.max(0, (request.likes_count ?? 0) + (wasLiked ? -1 : 1));
        ticket.likesCount = request.likes_count;
        ticket.liked = request.is_liked = !ticket.liked;
      }
    }
    finally {
      this.resetLoader();
    }
  }

  resetPage(): void {
    void this.goToPage(1);
  }

  firstPage(): void {
    if (this.page > 1 && !this.loader.isLoading) {
      void this.goToPage(1);
    }
  }

  prevPage(): void {
    if (this.page > 1 && !this.loader.isLoading) {
      void this.goToPage(this.page - 1);
    }
  }

  nextPage(): void {
    if (this.page < this.totalPages && !this.loader.isLoading) {
      void this.goToPage(this.page + 1);
    }
  }

  lastPage(): void {
    if (this.page < this.totalPages && !this.loader.isLoading) {
      void this.goToPage(this.totalPages);
    }
  }

  async goToPage(page: number): Promise<void> {
    if (page < 1 || this.loader.isLoading) {
      return;
    }
    this.setLoader('Loading tickets...');
    try {
      const pagination = this.hasSearchConditions
        ? await this.store.searchFeedbackPage({
          filters: this.searchFilters,
          sortby: this.selectedSortBy || undefined,
          sortOrder: this.selectedSortBy ? this.selectedSortOrder : undefined,
          page,
          rowsPerPage: this.rowsPerPage
        })
        : await this.store.loadFeedbackPage(page, this.rowsPerPage, this.store.selectedUseCaseIdValue);
      this.page = pagination.page;
      this.rowsPerPage = pagination.rowsPerPage;
      this.totalCount = pagination.totalCount;
      this.totalPages = pagination.totalPages;
      this.persistListState();
    } 
    finally {
      this.resetLoader();
    }
  }

  async onRowsPerPageChange(): Promise<void> {
    this.setLoader('Loading tickets...');
    try {
      const pagination = this.hasSearchConditions
        ? await this.store.searchFeedbackPage({
          filters: this.searchFilters,
          sortby: this.selectedSortBy || undefined,
          sortOrder: this.selectedSortBy ? this.selectedSortOrder : undefined,
          page: 1,
          rowsPerPage: this.rowsPerPage
        })
        : await this.store.loadFeedbackPage(1, this.rowsPerPage, this.store.selectedUseCaseIdValue);
      this.page = pagination.page;
      this.rowsPerPage = pagination.rowsPerPage;
      this.totalCount = pagination.totalCount;
      this.totalPages = pagination.totalPages;
      this.persistListState();
    } 
    finally {
      this.resetLoader();
    }
  }

  async exportTickets(): Promise<void> {
    if (this.loader.isLoading) {
      return;
    }
    this.setLoader('Exporting tickets...');
    try {
      const filters = Object.keys(this.searchFilters).length > 0 ? this.searchFilters : undefined;
      await this.store.exportFeedbackEntries(filters);
    }
    finally {
      this.resetLoader();
    }
  }

  get hasSearchConditions(): boolean {
    // Exclude usecase_id to check search filters are applied since it's from shell
    const { usecase_id, ...filtersWithoutUsecase } = this.searchFilters;
    return Object.keys(filtersWithoutUsecase).length > 0 || !!this.selectedSortBy;
  }

  get searchFilters(): Record<string, SearchFilterValue> {
    const filters: Record<string, SearchFilterValue> = {};
    if (this.selectedStatusId) {
      filters.status_id = this.selectedStatusId;
    }
    if (this.selectedRequestTypeId) {
      filters.workcategorytype_id = this.selectedRequestTypeId;
    }
    if (this.selectedApplicationId) {
      filters.application_id = this.selectedApplicationId;
    }
    if (this.selectedVersionId) {
      filters.version_id = this.selectedVersionId;
    }
    if (this.selectedTeamId) {
      filters.assigned_team = this.selectedTeamId;
    }
    if (this.searchTitle.trim()) {
      filters.title = this.searchTitle.trim();
    }
    const dateFilter = this.buildDateFilter();
    if (dateFilter) {
      filters.creation_date = dateFilter;
    }
    filters.usecase_id = this.store.selectedUseCaseIdValue ?? 0;
    return filters;
  }

  private buildDateFilter(): string[] | null {
    const range = this.selectedDateRange;
    if (!range || range.length === 0) {
      return null;
    }
    const [start, end] = range;
    if (!start) {
      return null;
    }
    const startDateStr = this.store.formatDateString(start);
    const endDateStr = end ? this.store.formatDateString(end) : null;
    if (!endDateStr || startDateStr === endDateStr) {
      return [startDateStr];
    }
    return [startDateStr, endDateStr];
  }

  private restoreListState(): void {
    const filterState = this.store.supportHubListFilterStateValue;
    const sortState = this.store.supportHubListSortStateValue;
    const paginationState = this.store.supportHubListPaginationStateValue;

    this.selectedStatusId = filterState.statusId;
    this.selectedRequestTypeId = filterState.requestTypeId;
    this.selectedApplicationId = filterState.applicationId;
    this.selectedVersionId = filterState.versionId;
    this.selectedTeamId = filterState.teamId;
    this.selectedDateRange = this.toDateRange(filterState.creationDate);
    this.showFilters = filterState.showFilters;

    this.selectedSortBy = sortState.sortBy;
    this.selectedSortOrder = sortState.sortOrder;
    this.page = paginationState.page;
    this.rowsPerPage = paginationState.rowsPerPage;
  }

  private persistListState(): void {
    this.store.setSupportHubListFilterState({
      statusId: this.selectedStatusId,
      requestTypeId: this.selectedRequestTypeId,
      applicationId: this.selectedApplicationId,
      versionId: this.selectedVersionId,
      teamId: this.selectedTeamId,
      creationDate: this.buildDateFilter(),
      showFilters: this.showFilters
    });
    this.store.setSupportHubListSortState({
      sortBy: this.selectedSortBy,
      sortOrder: this.selectedSortOrder
    });
    this.store.setSupportHubListPaginationState({
      page: this.page,
      rowsPerPage: this.rowsPerPage
    });
  }

  private toDateRange(dateRange: string[] | null): (Date | null)[] | null {
    if (!dateRange || dateRange.length === 0) {
      return null;
    }
    return [this.toDate(dateRange[0]), this.toDate(dateRange[1])];
  }

  private toDate(value: string): Date | null {
    const [year, month, day] = value?.split('-').map(part => Number(part)) ?? [];
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  private async loadVersions(): Promise<void> {
    const selectedUseCaseId = this.store.selectedUseCaseIdValue;
    if (!selectedUseCaseId) {
      this.versionsList = [];
      this.selectedVersionId = null;
      return;
    }

    await this.store.loadLookup();
    const response = await this.apiService.getFeedbackVersions(selectedUseCaseId);
    this.versionsList = Array.isArray(response?.result) ? response.result : [];

    if (this.selectedVersionId && !this.versionsList.some(version => version.version_id === this.selectedVersionId)) {
      this.selectedVersionId = null;
      this.persistListState();
    }
  }

  statusClass(status: string): string {
    if (this.store.isStatus(status, this.store.statusIds.NEW)) {
      return 'new';
    }
    if (this.store.isStatus(status, this.store.statusIds.IN_PROGRESS)) {
      return 'in-progress';
    }
    if (this.store.isStatus(status, this.store.statusIds.UAT_VALIDATING)) {
      return 'validation';
    }
    if (this.store.isStatus(status, this.store.statusIds.COMPLETE)) {
      return 'complete';
    }
    return status;
  }
}